import { ActivePromotion, IPromotion, PromotionType } from '../types/graphql'
import { IConfig, IPromotionUiCatalog } from '../types'
import { getComboPromotionUiDetails } from './getComboPromotionUiDetails'
import { getDirectPromotionUiDetails } from './getDirectPromotionUiDetails'
import { getVolumePromotionUiDetails } from './getVolumePromotionUiDetails'
import { getComplexPromotionUiDetails } from './getComplexPromotionUiDetails'
import {
  getActivePromotionsFields,
  getPromotionFields,
} from '../common/getPromotionFields'
import { getMultiPromotionTag } from './getMultiPromotionUiDetails'

/**
 * The three fields this function actually reads off a product.
 *
 * cinnamon types the parameter as `IProduct | ICartProduct`, which is a union of
 * two ~50-field interfaces. A catalog query returns neither — it returns
 * `IProductCatalog` — so the catalog page could only satisfy that signature with
 * a cast. Declaring the real requirement structurally is a widening: `IProduct`
 * and `ICartProduct` both still satisfy it, so every ported caller compiles
 * unchanged, and the mapper in `view-models/catalogItems.ts` can pass a raw
 * query result.
 */
export interface IPromotionSourceProduct {
  promotions?: IPromotion[] | null
  price?: number | null
  quantity?: number
}

export interface ISetPromotionUiDetailsCatalogArgs {
  product: IPromotionSourceProduct
  config?: IConfig
  activePromotions?: ActivePromotion[]
}
/**
 * Function declaration to set promotions ui details to be shown on catalog
 *
 * @internal
 * @param product - The product to be updated
 * @param categories - The categories
 * @returns IProduct updated with promotion ui details
 */
export const setPromotionUiDetailsCatalog = ({
  product,
  config,
  activePromotions,
}: ISetPromotionUiDetailsCatalogArgs): IPromotionUiCatalog | undefined => {
  if (product.promotions?.length && config) {
    const { promotions } = product

    if (promotions.length > 1) {
      return getMultiPromotionTag({ config, promotions })
    }

    const [promotionToBeDisplayed] = promotions

    const [activePromotion] =
      activePromotions?.filter((currentActivePromotion) => {
        const {
          fields: { customerGetsItems },
        } = getActivePromotionsFields(currentActivePromotion)
        const { customerGetsItems: customerGetsItemsToBeDisplayed } =
          getPromotionFields(promotionToBeDisplayed)

        const [customerGetsItem] = customerGetsItems
        const [customerGetsItemToBeDisplayed] = customerGetsItemsToBeDisplayed

        return customerGetsItem === customerGetsItemToBeDisplayed
      }) || []
    switch (promotionToBeDisplayed.type) {
      case PromotionType.DIRECT:
        return getDirectPromotionUiDetails({
          price: product.price || 0,
          config,
          promotion: promotionToBeDisplayed,
          activePromotion,
        })
      case PromotionType.VOLUME:
        return getVolumePromotionUiDetails({
          price: product.price || 0,
          quantity: product.quantity || 0,
          config,
          promotion: promotionToBeDisplayed,
          activePromotion,
        })
      case PromotionType.COMPLEX:
        return getComplexPromotionUiDetails({
          promotion: promotionToBeDisplayed,
          config,
          activePromotion,
          price: product.price || 0,
        })
      case PromotionType.COMBO:
        return getComboPromotionUiDetails({
          promotion: promotionToBeDisplayed,
          config,
          activePromotion,
        })
      default:
        break
    }
  }
}
